import { useCallback, useRef, useState } from "react";
import { PendingAddEntry } from "./nestedPendingAdds";

export interface NestedPendingAdds {
    pendingDraftIds: ReadonlySet<string>;
    gestureIdByDraftId: ReadonlyMap<string, string>;
    register: (entry: PendingAddEntry) => void;
    getEntries: () => readonly PendingAddEntry[];
    // Removes and returns the entry owning `draftId`, so the caller can either
    // write it or delete its draft rows.
    take: (draftId: string) => PendingAddEntry | null;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();
const EMPTY_GESTURES: ReadonlyMap<string, string> = new Map();

export const useNestedPendingAdds = (): NestedPendingAdds => {
    const entriesRef = useRef<PendingAddEntry[]>([]);
    const [pendingDraftIds, setPendingDraftIds] =
        useState<ReadonlySet<string>>(EMPTY_IDS);
    const [gestureIdByDraftId, setGestureIdByDraftId] =
        useState<ReadonlyMap<string, string>>(EMPTY_GESTURES);

    const sync = useCallback(() => {
        const draftIds = new Set<string>();
        const gestureIds = new Map<string, string>();
        for (const entry of entriesRef.current) {
            for (const target of entry.targets) {
                draftIds.add(target.draftId);
                gestureIds.set(target.draftId, entry.gestureId);
            }
        }
        setPendingDraftIds(draftIds);
        setGestureIdByDraftId(gestureIds);
    }, []);

    const register = useCallback(
        (entry: PendingAddEntry) => {
            entriesRef.current = [...entriesRef.current, entry];
            sync();
        },
        [sync],
    );

    const take = useCallback(
        (draftId: string): PendingAddEntry | null => {
            const entry = entriesRef.current.find((candidate) =>
                candidate.targets.some((target) => target.draftId === draftId),
            );
            if (entry === undefined) {
                return null;
            }
            entriesRef.current = entriesRef.current.filter(
                (candidate) => candidate !== entry,
            );
            sync();
            return entry;
        },
        [sync],
    );

    const getEntries = useCallback(() => entriesRef.current, []);

    return { pendingDraftIds, gestureIdByDraftId, register, getEntries, take };
};
